import classNames from 'classnames';
import { useRouter } from 'next/router';
import type { FunctionComponent } from 'react';
import { useMemo } from 'react';
import { Box } from '../../components/box';
import { Container } from '../../components/layout/grid';
import { SectionBlock } from '../../components/layout/section-block';
import { Markdown } from '../../components/markdown';
import { Row } from '../../components/row';
import { Text } from '../../components/text';
import { Thumbnail } from '../../components/thumbnail';
import type { SectionOfThumbnailsFragment } from '../../graphql/fragments/SectionOfThumbnailsFragment.graphql';
import type { ThemeColorFragment } from '../../graphql/fragments/ThemeColorFragment.graphql';
import type { ThumbnailFragment } from '../../graphql/fragments/ThumbnailFragment.graphql';
import type { ArticleKind } from '../../graphql/hooks/article-page';
import { useSectionOfThumbnails } from '../../graphql/hooks/section-of-thumbnails';
import { EMPTY_COLLECTION, EMPTY_SECTION } from '../../graphql/placeholders';
import { usePersonalizations } from '../../lib/hooks/usePersonalizations';
import { useUIStore } from '../../lib/store/useUIStore';
import { renderTemplate } from '../../lib/utils/text';
import { getAlternatingTheme } from '../../lib/utils/theme';
import { LINK } from '../../shared/util/link';
import type { HorizontalSide, VerticalSide } from '../../types/styles';
import type { EntryMeta } from '../../types/sys';
import { asVideoType } from '../../types/video';
import { ActionButton } from '../action-button';
import type { ActionProps } from '../action-button';
import { SelfFetchingRichText } from '../rich-text';
import styles from './section-of-thumbnails.module.scss';
export const SectionOfThumbnails: FunctionComponent<SectionOfThumbnailsFragment> = ({
  sys,
  type,
  ...restOfProps
}) => {
  if (restOfProps?.thumbnails) {
    return <SectionOfThumbnailsResolved sys={sys} type={type} {...restOfProps} />;
  }
  return <SelfFetchingSectionOfThumbnails sys={sys} type={type} data-sentry-element="SelfFetchingSectionOfThumbnails" data-sentry-component="SectionOfThumbnails" data-sentry-source-file="section-of-thumbnails.tsx" />;
};
const SelfFetchingSectionOfThumbnails: FunctionComponent<EntryMeta> = ({
  sys
}) => {
  const {
    section
  } = useSectionOfThumbnails(sys.id);
  return <SectionOfThumbnailsResolved {...section} data-sentry-element="SectionOfThumbnailsResolved" data-sentry-component="SelfFetchingSectionOfThumbnails" data-sentry-source-file="section-of-thumbnails.tsx" />;
};

/**
 * Renders a section of thumbnails with data from Contentful
 */
const SectionOfThumbnailsResolved: FunctionComponent<SectionOfThumbnailsFragment & {
  pad?: (VerticalSide | HorizontalSide)[];
}> = ({
  heading,
  headingSlug,
  copy,
  rowLength,
  thumbnails,
  centered,
  fillRow,
  section: {
    title,
    action
  } = EMPTY_SECTION,
  pad = []
}) => {
  const router = useRouter();
  const {
    webview
  } = useUIStore();
  const [first] = thumbnails?.items ?? [];
  const maxColumns = useMemo(() => {
    if (rowLength) return rowLength;
    if (!first) return 3;
    if ('type' in first && first.type === 'ArticleTopic') return 4;
    if ('size' in first && first.size === 'small') return 4;
    return 3;
  }, [first, rowLength]);
  return <div className={styles.outer} data-sentry-component="SectionOfThumbnailsResolved" data-sentry-source-file="section-of-thumbnails.tsx">
            <Container data-sentry-element="Container" data-sentry-source-file="section-of-thumbnails.tsx">
                <SectionBlock textAlignOnLargeScreen={centered ? 'center' : undefined} data-sentry-element="SectionBlock" data-sentry-source-file="section-of-thumbnails.tsx">
                    <Box pad={pad} data-sentry-element="Box" data-sentry-source-file="section-of-thumbnails.tsx">
                        {(heading || title) && <div className={styles.heading}>
                                <a href={LINK.money101Topic(headingSlug ?? '', webview.isWebview).serialize(router)}>
                                    <Markdown tag='div' typeStyle={{
                _: 'heading-xs',
                md: 'heading-sm'
              }} markdown={heading ?? title} />
                                </a>
                            </div>}
                        {!!copy && <div className={classNames(styles['copy-container'], centered && styles.centered)}>
                                <Text tag='div' typeStyle='body-lg'>
                                    {copy}
                                </Text>
                            </div>}

                        <Row fillRow={fillRow} maxColumns={maxColumns} className={classNames({
            [styles['row-with-heading']]: !!(heading || title)
          })} spacing='default' data-sentry-element="Row" data-sentry-source-file="section-of-thumbnails.tsx">
                            {(thumbnails?.items ?? []).map((thumbnail, index) => {
              return <div className={styles.thumbnail} key={thumbnail.sys.id}>
                                            <ThumbnailWrapper {...thumbnail} index={index} />
                                        </div>;
            })}
                        </Row>

                        <div className={styles.action}>
                            {action && <ActionButton {...action as ActionProps} />}
                        </div>
                    </Box>
                </SectionBlock>
            </Container>
        </div>;
};
const ThumbnailWrapper: FunctionComponent<ThumbnailFragment & {
  theme?: ThemeColorFragment;
  index: number;
}> = props => {
  const router = useRouter();
  const personalizations = usePersonalizations();
  const {
    webview
  } = useUIStore();
  if (props.type === 'Card') {
    const {
      content: {
        items: content = []
      } = EMPTY_COLLECTION
    } = props;
    return <div className={styles.card}>
                {content.map(text => <SelfFetchingRichText type='Text' key={text.sys.id} {...text} />)}
            </div>;
  }
  const {
    title,
    image,
    theme
  } = props;
  const variableProps = () => {
    if ('agency' in props) {
      const {
        agency,
        href
      } = props;
      return {
        subtitle: agency?.title,
        href,
        target: href,
        fit: 'cover' as const
      };
    }
    if ('slug' in props && 'category' in props) {
      const {
        category,
        slug = '',
        kind,
        video
      } = props;
      return {
        subtitle: category?.title,
        href: LINK.post(slug, kind as ArticleKind, webview.isWebview).serialize(router),
        fit: 'cover' as const,
        video: kind !== 'Video' ? video && asVideoType(video) : undefined // we dont want to play full videos, we want to show thumbnail
      };
    }
    if ('type' in props && props.type === 'Video') {
      return {
        video: asVideoType(props)
      };
    }
    if ('type' in props && props.type === 'ArticleTopic') {
      const {
        slug = ''
      } = props;
      return {
        href: LINK.money101Topic(slug, webview.isWebview).serialize(router),
        fit: 'cover' as const,
        title: ''
      };
    }
    if ('type' in props && props.type === 'ImageLink') {
      const {
        path
      } = props;
      const isExternalUrl = path?.startsWith('http');
      if (isExternalUrl) {
        return {
          subtitle: props.subtitle,
          href: path,
          openInNewWindow: true,
          target: '_blank',
          fit: 'cover' as const
        };
      }
      const link = LINK.cms({
        path
      });
      return {
        subtitle: props.subtitle,
        href: link.route,
        as: link.serialize(router),
        fit: 'cover' as const
      };
    }
    if ('action' in props) {
      const {
        action,
        size
      } = props;
      if (!action) return {
        size
      };
      if ('path' in action) {
        const link = LINK.cms(action);
        return {
          size,
          href: link.route,
          as: link.serialize(router)
        };
      }
      if ('url' in action) return {
        href: action.url,
        size
      };
      return {
        size
      };
    }
    if ('size' in props) {
      const size = props.size === 'small' ? 'small' : 'medium';
      return {
        size,
        subtitle: props.subtitle
      };
    }
    return {};
  };
  return <Thumbnail title={renderTemplate(title, personalizations)} theme={theme ?? getAlternatingTheme(props.index)} image={image} type={props.type} {...variableProps()} data-sentry-element="Thumbnail" data-sentry-component="ThumbnailWrapper" data-sentry-source-file="section-of-thumbnails.tsx" />;
};
export default SectionOfThumbnails;